/** @file Assets view: images and attachments; select focuses the Inspector; rename, menu and delete. */
import { useMemo } from 'react';
import { useStore } from 'zustand';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { TreePanelFooter } from '../../forms/TreePanelFooter';
import { useTreePanel } from '../../forms/useTreePanel';
import { focusStore } from '../../stores/focusStore';
import { selectionStore } from '../../stores/selectionStore';
import { ContextMenu } from '../../ui-kit/ContextMenu';
import { Tree } from '../../ui-kit/Tree';
import { usePanelKeys } from '../usePanelKeys';
import { assetActions as A } from './assetActions';
import { bindings } from './bindings';
import { buildAssets } from './buildAssets';
import '../../forms/forms.css';

/** Images start expanded; the selected row is the Inspector focus when it is an image or attachment. */
export function AssetsTree({ doc, t }: { doc: Doc; t: Translate }) {
  const nodes = useMemo(() => buildAssets(doc), [doc]);
  const focus = useStore(focusStore, (s) => s.focus);
  const selected = useMemo(() => new Set(focus === null ? [] : [focus.id]), [focus]);
  const s = useTreePanel(() => new Set(Object.keys(doc.images)));
  const keys = usePanelKeys('assets', bindings, { 'assets.delete': () => { const targets = A.deleteTargets(doc, focusStore.getState().focus); if (targets.length > 0) s.setDeleting(targets); } });
  const row = s.menu?.row ?? null;
  return (
    <div className="p2-tree-panel" data-testid="panel-assets" {...keys}>
      <Tree {...s.tree} nodes={nodes} selected={selected} label={t('panels.assets.tree')} renameLabel={t('panels.assets.rename')}
        onSelect={(id) => { focusStore.setState({ focus: A.focusOf(id) }); }}
        onRenameStart={(id) => { if (A.canRename(id)) s.setRenaming(id); }}
        onRenameCommit={(id, name) => { s.setRenaming(null); void s.run([A.renameCall(id, name)]); }} />
      <ContextMenu at={s.menu?.at ?? null} items={row === null ? [] : A.items(doc, row, selectionStore.getState(), t)} label={t('panels.assets.menu')}
        onClose={() => { s.setMenu(null); }} onSelect={(item) => { if (row !== null) s.perform(A.action(doc, row, item, selectionStore.getState()), row); }} />
      <TreePanelFooter doc={doc} panel={s} t={t} />
    </div>
  );
}
